import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  MessageCircle,
  Send,
  Paperclip,
  Camera,
  Users,
  ShieldAlert,
  X,
  Image as ImageIcon,
  Film,
  Check,
  Mic,
  StopCircle,
  Trash2,
} from "lucide-react";

const IST_TIME = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
};

const IST_DAY = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
};

const AVATAR_COLORS = [
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-teal-500",
  "bg-sky-500",
  "bg-indigo-500",
  "bg-fuchsia-500",
  "bg-pink-500",
];
const avatarColor = (id = "") => {
  let n = 0;
  for (const c of id) n = (n + c.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[n];
};
const initials = (name = "?") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const mediaSrc = (relUrl) => (relUrl ? `${backendBase}${relUrl}` : "");

function DateChip({ day }) {
  return (
    <div className="flex justify-center my-3">
      <span className="mono text-[10px] uppercase tracking-widest text-slate-600 bg-white/70 border border-slate-200 rounded-full px-3 py-0.5">
        {day}
      </span>
    </div>
  );
}

function MessageBubble({ msg, mine, canDelete, onDelete }) {
  const bg = mine ? "bg-[#DCF8C6]" : "bg-white";
  const align = mine ? "items-end" : "items-start";
  const nameColor = mine ? "text-emerald-800" : "text-[#0055FF]";
  const isMedia = msg.kind === "image" || msg.kind === "video" || msg.kind === "audio";
  const expired = !!msg.media_expired || (isMedia && !msg.media_url);
  return (
    <div className={`flex ${align} flex-col mb-1.5`}>
      <div
        className={`group relative max-w-[75%] rounded-2xl ${bg} shadow-sm px-3 py-1.5`}
        data-testid={`chat-msg-${msg.id}`}
      >
        {canDelete && (
          <button
            type="button"
            onClick={() => onDelete?.(msg)}
            className={
              "absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 " +
              "text-slate-400 hover:text-red-600 hover:border-red-300 shadow-sm " +
              "opacity-0 group-hover:opacity-100 transition-opacity"
            }
            title="Delete message"
            data-testid={`chat-msg-delete-${msg.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
        {!mine && (
          <div className={`mono text-[10px] font-bold ${nameColor} mb-0.5`}>
            {msg.sender_name}
            {msg.sender_role === "owner" && (
              <span className="ml-1 text-amber-700 bg-amber-100 rounded px-1 py-[1px] text-[9px] uppercase">
                owner
              </span>
            )}
            {msg.sender_role === "customer" && (
              <span className="ml-1 text-sky-700 bg-sky-100 rounded px-1 py-[1px] text-[9px] uppercase">
                customer
              </span>
            )}
          </div>
        )}
        {isMedia && expired ? (
          // Media auto-deleted after 48h — keep a visible placeholder so the
          // conversation still reads correctly.
          <div className="rounded-lg mb-1 -mx-1 px-3 py-2 bg-slate-100 text-slate-500 text-[12px] italic flex items-center gap-2">
            {msg.kind === "audio" ? (
              <Mic className="w-4 h-4" />
            ) : msg.kind === "video" ? (
              <Film className="w-4 h-4" />
            ) : (
              <ImageIcon className="w-4 h-4" />
            )}
            <span>{msg.media_name || `${msg.kind} attachment`} · expired (48h)</span>
          </div>
        ) : isMedia && msg.media_url ? (
          <div className="rounded-lg overflow-hidden mb-1 -mx-1">
            {msg.kind === "image" ? (
              <img
                src={mediaSrc(msg.media_url)}
                alt={msg.media_name || "image"}
                className="max-h-[320px] w-auto rounded-lg cursor-pointer"
                onClick={() => window.open(mediaSrc(msg.media_url), "_blank")}
              />
            ) : msg.kind === "video" ? (
              <video
                src={mediaSrc(msg.media_url)}
                controls
                className="max-h-[320px] w-full rounded-lg bg-black"
                preload="metadata"
              />
            ) : (
              <audio
                src={mediaSrc(msg.media_url)}
                controls
                className="w-full mt-1"
                preload="metadata"
                data-testid={`chat-audio-${msg.id}`}
              />
            )}
          </div>
        ) : null}
        {msg.text && (
          <div className="text-[14px] leading-snug whitespace-pre-wrap break-words text-slate-800">
            {msg.text}
          </div>
        )}
        <div className="flex justify-end items-center gap-0.5 mt-0.5">
          <span className="mono text-[9px] text-slate-500">
            {IST_TIME(msg.created_at)}
          </span>
          {mine && <Check className="w-3 h-3 text-emerald-600" />}
        </div>
      </div>
    </div>
  );
}

export default function Chat({ compact = false }) {
  const { user } = useAuth();
  const [access, setAccess] = useState(null); // {has_access, role, owner_id}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [file, setFile] = useState(null); // File
  const [preview, setPreview] = useState(""); // dataURL for preview
  const [loading, setLoading] = useState(true);
  // Voice-note recorder — captures a webm audio clip via MediaRecorder and
  // wraps it in a File so the existing send() path can attach it as-is.
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const recorderChunksRef = useRef([]);

  // Owner-only: manage-members drawer
  const [manageOpen, setManageOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [memberIds, setMemberIds] = useState([]);
  const [savingMembers, setSavingMembers] = useState(false);

  const listRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const isOwner = user?.role === "owner";

  // Load access first.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chat/access");
        setAccess(data);
      } catch (e) {
        setAccess({ has_access: false });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages?limit=200");
      setMessages(data.messages || []);
    } catch (e) {
      // silent — the access check surfaces the real error
    }
  }, []);

  useEffect(() => {
    if (!access?.has_access) return;
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [access?.has_access, fetchMessages]);

  // Auto-scroll to newest.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/config");
      setEmployees(data.employees || []);
      setMemberIds(data.member_ids || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load members");
    }
  }, []);

  const openManage = () => {
    setManageOpen(true);
    loadConfig();
  };

  const toggleMember = (id) => {
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const saveMembers = async () => {
    setSavingMembers(true);
    try {
      await api.put("/chat/config", { member_ids: memberIds });
      toast.success("Members updated");
      setManageOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSavingMembers(false);
    }
  };

  const pickFile = () => fileInputRef.current?.click();

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // let user re-pick the same file later
    if (!f) return;
    const okMime = /^(image|video|audio)\//.test(f.type);
    if (!okMime) return toast.error("Only images, audio and video are allowed.");
    const MAX = 15 * 1024 * 1024;
    if (f.size > MAX) return toast.error("File too large. Max 15 MB.");
    setFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview("");
    }
  };

  const startRecording = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice notes need microphone access — try a modern browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefer webm/opus (widely supported). Falls back to whatever the
      // browser picks so old Android Chrome / iOS Safari still work.
      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "",
      ];
      const chosen = mimeCandidates.find(
        (m) => !m || (window.MediaRecorder && MediaRecorder.isTypeSupported(m))
      );
      const rec = new MediaRecorder(stream, chosen ? { mimeType: chosen } : undefined);
      recorderChunksRef.current = [];
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size) recorderChunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        const blobType = rec.mimeType || "audio/webm";
        const blob = new Blob(recorderChunksRef.current, { type: blobType });
        const ext = blobType.includes("mp4") ? "m4a" : "webm";
        const audioFile = new File([blob], `voice-note-${Date.now()}.${ext}`, {
          type: blobType,
        });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size < 512) {
          toast.error("Voice note too short — try again.");
          return;
        }
        setFile(audioFile);
        setPreview("");
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      toast.error(
        err?.name === "NotAllowedError"
          ? "Microphone permission denied. Enable it and try again."
          : "Could not start recording."
      );
    }
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop();
    }
    recorderRef.current = null;
    setRecording(false);
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const clearFile = () => {
    setFile(null);
    setPreview("");
  };

  const send = async () => {
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const form = new FormData();
      if (text.trim()) form.append("text", text.trim());
      if (file) form.append("file", file);
      const { data } = await api.post("/chat/messages", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Optimistically append; poll will reconcile.
      setMessages((prev) => [...prev, data]);
      setText("");
      clearFile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  // Owner-only moderation. Confirms in the browser, then hits the DELETE
  // endpoint. On success we drop the message from local state so the deletion
  // feels instant — the 5-sec poll would eventually reconcile anyway.
  const deleteMessage = useCallback(
    async (msg) => {
      if (!msg?.id) return;
      const summary = msg.text
        ? `"${msg.text.slice(0, 60)}${msg.text.length > 60 ? "…" : ""}"`
        : msg.media_name
          ? `the ${msg.kind || "media"} "${msg.media_name}"`
          : `this ${msg.kind || "message"}`;
      if (!window.confirm(`Delete ${summary} from ${msg.sender_name}?\nThis cannot be undone.`)) {
        return;
      }
      try {
        await api.delete(`/chat/messages/${msg.id}`);
        setMessages((prev) => prev.filter((m) => m.id !== msg.id));
        toast.success("Message deleted");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not delete message");
      }
    },
    []
  );

  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const day = IST_DAY(m.created_at);
      if (day !== lastDay) {
        out.push({ _sep: true, day, id: `sep-${day}` });
        lastDay = day;
      }
      out.push(m);
    }
    return out;
  }, [messages]);

  if (loading) {
    return (
      <div className={compact ? "p-4" : "p-6"}>
        <div className="text-sm text-slate-500">Loading chat…</div>
      </div>
    );
  }

  if (!access?.has_access) {
    return (
      <div className={compact ? "p-4" : "p-6"}>
        <Card className="border-slate-200 shadow-none max-w-md mx-auto">
          <CardContent className="p-6 text-center space-y-3">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 grid place-items-center">
              <ShieldAlert className="w-6 h-6 text-amber-700" />
            </div>
            <div className="font-heading font-bold text-lg">
              You&apos;re not in the team chat yet
            </div>
            <p className="text-sm text-slate-600">
              Only employees your owner has selected can see and post messages
              in the team chat. Ask your owner to add you.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const rootCls = compact
    ? "h-[calc(100vh-8rem)] flex flex-col bg-[#e5ddd5]"
    : "h-[calc(100vh-4rem)] flex flex-col bg-[#e5ddd5]";

  return (
    <div className={rootCls} data-testid="chat-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white shadow-sm">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-9 h-9 rounded-full bg-[#128C7E] grid place-items-center">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Team Chat</div>
            <div className="mono text-[10px] opacity-80">
              {messages.length} message{messages.length !== 1 ? "s" : ""}
            </div>
          </div>
          {isOwner && (
            <Button
              size="sm"
              onClick={openManage}
              className="bg-white/15 hover:bg-white/25 text-white border-0"
              data-testid="chat-manage-members"
            >
              <Users className="w-4 h-4 mr-1" />
              Members
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto px-3 py-4 space-y-0"
        style={{
          backgroundImage:
            "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22 opacity=%220.05%22><circle cx=%2260%22 cy=%2260%22 r=%2255%22 fill=%22%23075E54%22/></svg>')",
        }}
        data-testid="chat-messages"
      >
        {grouped.length === 0 && (
          <div className="text-center text-slate-500 text-sm mt-10">
            No messages yet — say hi to your team.
          </div>
        )}
        {grouped.map((m) => {
          if (m._sep) return <DateChip key={m.id} day={m.day} />;
          const mine = m.sender_id === user?.id;
          if (mine) {
            return (
              <div key={m.id} className="flex justify-end">
                <div className="w-full flex flex-col items-end">
                  <MessageBubble
                    msg={m}
                    mine
                    canDelete={isOwner}
                    onDelete={deleteMessage}
                  />
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className="flex gap-2 items-end">
              <div
                className={`w-8 h-8 rounded-full ${avatarColor(m.sender_id)} text-white grid place-items-center text-xs font-bold shrink-0`}
              >
                {initials(m.sender_name)}
              </div>
              <div className="flex-1 min-w-0">
                <MessageBubble
                  msg={m}
                  mine={false}
                  canDelete={isOwner}
                  onDelete={deleteMessage}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="bg-[#f0f2f5] border-t border-slate-200 p-2">
        {file && (
          <div className="mb-2 flex items-center gap-2 bg-white rounded-md p-2 border border-slate-200">
            {preview ? (
              <img
                src={preview}
                alt="preview"
                className="w-12 h-12 object-cover rounded"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-slate-100 grid place-items-center text-slate-500">
                {file.type.startsWith("audio/") ? (
                  <Mic className="w-5 h-5" />
                ) : (
                  <Film className="w-5 h-5" />
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{file.name}</div>
              <div className="mono text-[10px] text-slate-500">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </div>
            </div>
            <button
              type="button"
              onClick={clearFile}
              className="text-slate-500 hover:text-red-600 p-1"
              data-testid="chat-clear-file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={pickFile}
            className="w-10 h-10 rounded-full bg-white grid place-items-center text-slate-600 hover:text-[#0055FF] border border-slate-200"
            data-testid="chat-attach"
            aria-label="attach media"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,audio/*"
            className="hidden"
            onChange={onFilePicked}
            data-testid="chat-file-input"
          />
          {/* Voice-note recorder — press-and-record, tap-stop to attach. */}
          <button
            type="button"
            onClick={toggleRecording}
            disabled={sending || !!file}
            className={
              "w-10 h-10 rounded-full grid place-items-center border transition disabled:opacity-40 " +
              (recording
                ? "bg-red-500 border-red-500 text-white animate-pulse"
                : "bg-white border-slate-200 text-slate-600 hover:text-[#0055FF]")
            }
            data-testid="chat-voice-toggle"
            aria-label={recording ? "stop recording" : "record voice note"}
            title={recording ? "Stop & attach" : "Record voice note"}
          >
            {recording ? <StopCircle className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          {/* Mobile-only shortcut to open the rear camera directly. */}
          <label
            className="w-10 h-10 rounded-full bg-white grid place-items-center text-slate-600 hover:text-[#0055FF] border border-slate-200 cursor-pointer"
            data-testid="chat-camera-label"
            aria-label="take photo"
          >
            <Camera className="w-4 h-4" />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onFilePicked}
              data-testid="chat-camera-input"
            />
          </label>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Type a message"
            className="flex-1 bg-white"
            data-testid="chat-input"
          />
          <Button
            onClick={send}
            disabled={sending || (!text.trim() && !file)}
            className="bg-[#075E54] hover:bg-[#128C7E] w-10 h-10 p-0 rounded-full"
            data-testid="chat-send"
            aria-label="send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Manage members dialog (owner-only) */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent
          className="sm:max-w-md max-h-[80vh] overflow-y-auto"
          data-testid="chat-members-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">Team chat members</DialogTitle>
            <DialogDescription>
              Pick which employees see and can post to the team chat. Everyone
              you tick can send text, images and videos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 py-2">
            {employees.length === 0 && (
              <div className="text-sm text-slate-500 text-center py-6">
                No employees to add yet.
              </div>
            )}
            {employees.map((e) => {
              const checked = memberIds.includes(e.id);
              return (
                <label
                  key={e.id}
                  className={
                    "flex items-center gap-3 p-2 rounded-md border cursor-pointer " +
                    (checked
                      ? "border-[#0055FF] bg-blue-50/50"
                      : "border-slate-200 hover:bg-slate-50")
                  }
                  data-testid={`chat-member-toggle-${e.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleMember(e.id)}
                    className="w-4 h-4"
                  />
                  <div
                    className={`w-8 h-8 rounded-full ${avatarColor(e.id)} text-white grid place-items-center text-xs font-bold`}
                  >
                    {initials(e.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.name}</div>
                    <div className="mono text-[10px] text-slate-500 truncate">
                      {e.department} · {e.email}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setManageOpen(false)}
              disabled={savingMembers}
            >
              Cancel
            </Button>
            <Button
              onClick={saveMembers}
              disabled={savingMembers}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="chat-members-save"
            >
              {savingMembers ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
